import React from 'react';
import { FiLock } from 'react-icons/fi';

interface LockedFeatureOverlayProps {
	/** When true the children are dimmed, greyed and made non-interactive. */
	locked: boolean;
	/** Also draw the centered lock badge (Figma: the clinical-team card carries one). */
	showLock?: boolean;
	id?: string;
	/** Layout classes for the box that holds the children (e.g. `flex flex-col gap-y-[30px]`). */
	className?: string;
	children: React.ReactNode;
}

/**
 * Wraps a dashboard section that is unavailable while the Care+ plan is ended.
 * Always renders the same two-level DOM so switching `locked` never reflows the grid;
 * only the dim classes and the badge toggle. The badge lives outside the dimmed box so it
 * stays fully opaque.
 */
const LockedFeatureOverlay: React.FC<LockedFeatureOverlayProps> = ({
	locked,
	showLock = false,
	id,
	className = '',
	children,
}) => (
	<div id={id} className="relative" aria-disabled={locked || undefined} data-locked={locked || undefined}>
		<div
			className={`${className} ${locked ? 'opacity-50 grayscale pointer-events-none select-none' : ''}`}
			aria-hidden={locked || undefined}
		>
			{children}
		</div>
		{locked && showLock && (
			<div className="absolute inset-0 flex items-center justify-center pointer-events-none">
				<span className="flex items-center justify-center w-[84px] h-[84px] rounded-full bg-[#8d8d8d]/85 text-white shadow-md">
					<FiLock size={40} />
				</span>
			</div>
		)}
	</div>
);

export default LockedFeatureOverlay;
